import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useGetTenantConfigQuery } from '../../apis/common';
import { useBlockingOverlayState, useCurrentPageTitle } from '../../hooks';
import { FeedbackPlaneIcon } from '../svg';
import useFeedbackTabPosition from './useFeedbackTabPosition';
import FeedbackDrawer from './FeedbackDrawer';
import { feedbackOpenEvent } from '../../services/analyticsService';

const TAB_SHADOW_OFFSET = 4;

// Persistent FEEDBACK tab pinned to the inline-end viewport edge. FeedbackDrawer (and its
// discard-confirm) stays mounted even while this component hides the tab element itself — hiding
// the whole subtree here would unmount the very drawer that's the reason the tab is hidden.
const FeedbackTab = ({ isBannerVisible, isMemberArea, headerHeight, fabVisible }) => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.app.loginUser);
  const { rtl, locale } = useSelector((state) => state.app.AppConfig);
  const { data: config } = useGetTenantConfigQuery(undefined, { skip: !user?.id });
  const { isOpen: blockingOverlayOpen } = useBlockingOverlayState();
  const pageTitle = useCurrentPageTitle();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const drawerRef = useRef();

  const { y, dragging, moved, handlers } = useFeedbackTabPosition({
    userId: user?.id,
    isMemberArea,
    isBannerVisible,
    headerHeight,
    fabVisible,
  });

  // Signed out, or the tenant flag is off: render nothing at all, including the drawer.
  if (!user?.id || !config?.enabled) return null;

  const openDrawer = () => {
    setDrawerOpen(true);
    drawerRef.current?.openDrawer();
    feedbackOpenEvent(user, pageTitle || undefined, locale);
  };

  const handleClick = () => {
    if (!moved) openDrawer();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openDrawer();
    }
  };

  // box-shadow offsets are physical, not logical — CSS has no inline-aware shadow direction, so
  // this is the one place that still needs an explicit rtl branch (T7).
  const shadowSign = rtl ? 1 : -1;

  const tabStyle = {
    position: 'fixed',
    insetInlineEnd: 0,
    top: `${(y * 100).toFixed(2)}%`,
    transform: 'translateY(-50%)',
    background: '#E1F2F0',
    color: '#006169',
    border: '1px solid #B3D0D2',
    borderInlineEnd: 'none',
    // Logical corner properties auto-flip with the <html dir> attribute useAppInit sets, so the
    // rounded corners always face inward without a manual rtl branch here.
    borderStartStartRadius: 10,
    borderEndStartRadius: 10,
    padding: '14px 8px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    cursor: dragging ? 'grabbing' : 'pointer',
    touchAction: 'none',
    userSelect: 'none',
    zIndex: 1100,
    boxShadow: `${shadowSign * TAB_SHADOW_OFFSET}px 4px 14px rgba(0,97,105,${dragging ? 0.22 : 0.12})`,
    transition: dragging ? 'none' : 'top 140ms ease, box-shadow 140ms ease',
  };

  return (
    <>
      {!drawerOpen && !blockingOverlayOpen && (
        <div
          role="button"
          tabIndex={0}
          aria-label={t('FEEDBACK')}
          title={t('Feedback — drag to move')}
          style={tabStyle}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          {...handlers}
        >
          <FeedbackPlaneIcon size={17} />
          <span style={{ writingMode: 'vertical-rl', fontSize: 12, fontWeight: 700, letterSpacing: '.08em' }}>
            {t('FEEDBACK')}
          </span>
          <span style={{ display: 'flex', flexDirection: 'column', gap: 2, opacity: 0.75, paddingTop: 2 }}>
            {[0, 1, 2].map((row) => (
              <span key={row} style={{ display: 'flex', gap: 2 }}>
                <span style={{ width: 3, height: 3, borderRadius: '50%', background: 'currentColor' }} />
                <span style={{ width: 3, height: 3, borderRadius: '50%', background: 'currentColor' }} />
              </span>
            ))}
          </span>
        </div>
      )}

      <FeedbackDrawer
        ref={drawerRef}
        user={user}
        locale={locale}
        pageSection={pageTitle}
        config={config}
        onCloseDrawer={() => setDrawerOpen(false)}
      />
    </>
  );
};

export default FeedbackTab;
